/**
 * HTML Sanitization utilities for preventing XSS attacks
 * Uses regex-based sanitization that works on both server and client
 */

// Safe HTML tags that are allowed
const ALLOWED_TAGS = new Set([
  'a', 'abbr', 'b', 'blockquote', 'br', 'caption', 'cite', 'code',
  'col', 'colgroup', 'dd', 'del', 'details', 'dfn', 'div', 'dl', 'dt',
  'em', 'figcaption', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'hr', 'i', 'img', 'ins', 'kbd', 'li', 'mark', 'ol', 'p', 'picture',
  'pre', 'q', 's', 'samp', 'small', 'source', 'span', 'strong', 'sub',
  'summary', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead',
  'time', 'tr', 'u', 'ul', 'var', 'wbr',
  'video', 'audio', 'iframe',
]);

// Safe attributes that are allowed
const ALLOWED_ATTRS = new Set([
  'href', 'src', 'alt', 'title', 'class', 'id', 'name', 'target', 'rel',
  'width', 'height', 'style', 'role', 'tabindex',
  'loading', 'decoding', 'srcset', 'sizes', 'poster', 'controls',
  'autoplay', 'loop', 'muted', 'playsinline', 'preload',
  'frameborder', 'allowfullscreen', 'allow',
  'colspan', 'rowspan', 'headers', 'scope',
  'datetime', 'cite', 'lang', 'dir',
]);

// Dangerous event handler attributes to remove
const DANGEROUS_ATTRS = [
  'onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur',
  'onchange', 'onsubmit', 'onkeydown', 'onkeyup', 'onkeypress',
  'onmouseenter', 'onmouseleave', 'onmousedown', 'onmouseup',
  'ondblclick', 'oncontextmenu', 'onwheel', 'ondrag', 'ondrop',
  'oncopy', 'oncut', 'onpaste', 'onscroll', 'onresize',
  'oninput', 'onselect', 'ontouchstart', 'ontouchend', 'ontouchmove',
];

// Dangerous tags to completely remove (including content)
const DANGEROUS_TAGS = ['script', 'noscript', 'object', 'embed', 'applet'];

// Tags to remove but keep content
const STRIP_TAGS = ['style', 'form', 'input', 'button', 'select', 'textarea'];

/**
 * Remove dangerous event handler attributes from a tag
 */
function removeEventHandlers(tag: string): string {
  let result = tag;
  for (const attr of DANGEROUS_ATTRS) {
    // Match attribute with various quote styles and values
    const patterns = [
      new RegExp(`\\s${attr}\\s*=\\s*"[^"]*"`, 'gi'),
      new RegExp(`\\s${attr}\\s*=\\s*'[^']*'`, 'gi'),
      new RegExp(`\\s${attr}\\s*=\\s*[^\\s>]+`, 'gi'),
    ];
    for (const pattern of patterns) {
      result = result.replace(pattern, '');
    }
  }
  return result;
}

/**
 * Sanitize href/src attributes to prevent javascript: URLs
 */
function sanitizeUrlAttribute(tag: string): string {
  // Remove javascript:, vbscript:, and data:text/html URLs
  return tag
    .replace(/href\s*=\s*["']?\s*javascript:[^"'>\s]*/gi, 'href="#"')
    .replace(/href\s*=\s*["']?\s*vbscript:[^"'>\s]*/gi, 'href="#"')
    .replace(/href\s*=\s*["']?\s*data:text\/html[^"'>\s]*/gi, 'href="#"')
    .replace(/src\s*=\s*["']?\s*javascript:[^"'>\s]*/gi, 'src=""')
    .replace(/src\s*=\s*["']?\s*vbscript:[^"'>\s]*/gi, 'src=""')
    .replace(/src\s*=\s*["']?\s*data:text\/html[^"'>\s]*/gi, 'src=""');
}

/**
 * Sanitize style attribute to prevent expression() and url() attacks
 */
function sanitizeStyleAttribute(tag: string): string {
  return tag.replace(
    /style\s*=\s*["']([^"']*)["']/gi,
    (match, styleContent) => {
      // Remove dangerous CSS expressions
      const sanitizedStyle = styleContent
        .replace(/expression\s*\([^)]*\)/gi, '')
        .replace(/javascript\s*:/gi, '')
        .replace(/behavior\s*:/gi, '')
        .replace(/-moz-binding\s*:/gi, '')
        // Allow url() but block javascript: inside it
        .replace(/url\s*\(\s*["']?\s*javascript:[^)]*\)/gi, '');
      return `style="${sanitizedStyle}"`;
    }
  );
}

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param dirty - The untrusted HTML string
 * @returns Sanitized HTML string safe for dangerouslySetInnerHTML
 */
export function sanitizeHtml(dirty: string | null | undefined): string {
  if (!dirty) return '';

  let html = dirty;

  // 1. Remove dangerous tags completely (including their content)
  for (const tag of DANGEROUS_TAGS) {
    const pattern = new RegExp(`<${tag}[^>]*>[\\s\\S]*?<\\/${tag}>`, 'gi');
    html = html.replace(pattern, '');
    // Also remove self-closing versions
    html = html.replace(new RegExp(`<${tag}[^>]*/?>`, 'gi'), '');
  }

  // 2. Remove strip tags but keep their content
  for (const tag of STRIP_TAGS) {
    html = html.replace(new RegExp(`<${tag}[^>]*>`, 'gi'), '');
    html = html.replace(new RegExp(`</${tag}>`, 'gi'), '');
  }

  // 3. Process remaining tags
  html = html.replace(/<([a-z][a-z0-9]*)\s*([^>]*)>/gi, (match, tagName, attributes) => {
    const lowerTag = tagName.toLowerCase();

    // Remove unknown tags
    if (!ALLOWED_TAGS.has(lowerTag)) {
      return '';
    }

    // Sanitize attributes
    let sanitizedAttrs = attributes;
    sanitizedAttrs = removeEventHandlers(` ${sanitizedAttrs}`).trim();
    sanitizedAttrs = sanitizeUrlAttribute(sanitizedAttrs);
    sanitizedAttrs = sanitizeStyleAttribute(sanitizedAttrs);

    // Filter to only allowed attributes
    const attrMatches = sanitizedAttrs.match(/([a-z][a-z0-9-]*)\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi) || [];
    const safeAttrs = attrMatches.filter((attr: string) => {
      const attrName = attr.split('=')[0].trim().toLowerCase();
      return ALLOWED_ATTRS.has(attrName) || attrName.startsWith('data-') || attrName.startsWith('aria-');
    });

    return safeAttrs.length > 0 ? `<${lowerTag} ${safeAttrs.join(' ')}>` : `<${lowerTag}>`;
  });

  // 4. Process closing tags
  html = html.replace(/<\/([a-z][a-z0-9]*)>/gi, (match, tagName) => {
    const lowerTag = tagName.toLowerCase();
    return ALLOWED_TAGS.has(lowerTag) ? `</${lowerTag}>` : '';
  });

  // 5. Remove any remaining HTML comments (can contain attacks)
  html = html.replace(/<!--[\s\S]*?-->/g, '');

  // 6. Remove CDATA sections
  html = html.replace(/<!\[CDATA\[[\s\S]*?\]\]>/g, '');

  return html;
}

/**
 * Sanitize HTML and return an object for dangerouslySetInnerHTML
 * @param dirty - The untrusted HTML string
 * @returns Object with __html property for React's dangerouslySetInnerHTML
 */
export function createSafeHtml(dirty: string | null | undefined): { __html: string } {
  return { __html: sanitizeHtml(dirty) };
}

/**
 * Strict sanitization - removes all HTML tags, returns plain text
 * Use for user-generated content that should never contain HTML
 * @param dirty - The untrusted string
 * @returns Plain text with all HTML removed
 */
export function sanitizeToText(dirty: string | null | undefined): string {
  if (!dirty) return '';
  // Remove all HTML tags
  return dirty
    .replace(/<[^>]*>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

/**
 * Sanitize URL to prevent javascript: and data: URI attacks
 * @param url - The untrusted URL
 * @returns Sanitized URL or empty string if unsafe
 */
export function sanitizeUrl(url: string | null | undefined): string {
  if (!url) return '';

  const trimmed = url.trim().toLowerCase();

  // Block dangerous protocols
  if (
    trimmed.startsWith('javascript:') ||
    trimmed.startsWith('vbscript:') ||
    trimmed.startsWith('data:text/html')
  ) {
    return '';
  }

  return url;
}

export default {
  sanitizeHtml,
  createSafeHtml,
  sanitizeToText,
  sanitizeUrl,
};
